<template>
  <div
    class="w-50 compare-pane h-100 overflow-y-auto"
    :class="{
      'compare-pane-diff': isDiffPane,
      'compare-pane-left': rightBorder,
    }"
    role="region"
    :aria-label="ariaLabel"
  >
    <div class="pane-inner pa-6">
      <div class="text-break py-2">
        <div
          v-if="titleHtml != null"
          class="text-h5 font-weight-medium"
        >
          <DpplSafeHtml :html="titleHtml" />
        </div>
        <div
          v-else
          class="text-h5 font-weight-medium"
        >
          {{ profile?.title || '' }}
        </div>
      </div>
      <div class="text-body-2 text-medium-emphasis mb-2">
        {{ subtitle }}
      </div>
      <div class="d-flex align-center ga-2 flex-wrap mb-2">
        <span class="text-caption text-medium-emphasis">
          Effective {{ formatDisplayDate(profile?.effective_date) }}
        </span>
        <span
          v-if="getQuarterYearLabel(profile?.effective_date)"
          class="revision-badge revision-badge-quarter text-caption"
        >
          {{ getQuarterYearLabel(profile?.effective_date) }}
        </span>
        <span
          v-if="isCurrentVersion"
          class="current-version-badge text-caption text-info rounded-pill"
        >
          Current version
        </span>
      </div>
      <div
        v-if="acknowledgedAt"
        class="text-caption text-medium-emphasis mb-1"
      >
        Employee Acknowledged on {{ formatDisplayDate(acknowledgedAt) }}
      </div>

      <div
        v-for="field in fields"
        :key="field.type"
        class="py-2"
      >
        <div class="d-flex justify-end mt-2 mb-1">
          <FieldLabel>{{ field.label }}</FieldLabel>
        </div>
        <DocumentPreviewFieldHtml class="field-content">
          <div
            v-if="field.headerHtml && !isEmptyHtml(field.headerHtml)"
            class="locked-field"
          >
            <DpplSafeHtml :html="field.headerHtml" />
          </div>
          <DpplSafeHtml
            v-if="field.html"
            :html="field.html"
          />
          <div
            v-if="field.footerHtml && !isEmptyHtml(field.footerHtml)"
            class="locked-field"
          >
            <DpplSafeHtml :html="field.footerHtml" />
          </div>
        </DocumentPreviewFieldHtml>
      </div>
    </div>
  </div>
</template>

<script setup>
import FieldLabel from '@/components/editor/field/FieldLabel.vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import DocumentPreviewFieldHtml from '@ui/components/general/DocumentPreviewFieldHtml/DocumentPreviewFieldHtml.vue';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import { formatDisplayDate, getQuarterYearLabel } from '@/helpers/date';

defineProps({
  profile: {
    type: Object,
    default: null,
  },
  fields: {
    type: Array,
    default: () => [],
  },
  subtitle: {
    type: String,
    default: '',
  },
  isCurrentVersion: {
    type: Boolean,
    default: false,
  },
  isDiffPane: {
    type: Boolean,
    default: false,
  },
  // Diff HTML for the title. Don't pass to use profile.title
  titleHtml: {
    type: String,
    default: undefined,
  },
  acknowledgedAt: {
    type: String,
    default: null,
  },
  ariaLabel: {
    type: String,
    required: true,
  },
  rightBorder: {
    type: Boolean,
    default: false,
  },
});
</script>

<style scoped lang="less">
.compare-pane {
  min-height: 0;
}

.compare-pane-left {
  border-right: 1px solid @stroke-default;
}

.compare-pane-diff {
  :deep(.removed-diff) {
    background: @red-200;
    text-decoration: line-through;
  }
  :deep(.added-diff) {
    background: @green-200;
  }
}

.field-content {
  :deep(h1), :deep(h2), :deep(h3) {
    line-height: 28px;
    font-weight: 700;
    padding: 8px 40px 8px 0px;
  }
  :deep(h1) {
    font-size: 24px;
  }
  :deep(h2) {
    font-size: 20px;
  }
  :deep(h3) {
    font-size: 18px;
  }
  :deep(p), :deep(section), :deep(article) {
    font-size: 16px;
    line-height: 28px;
    font-weight: 300;
    padding: 8px 40px 8px 0px;
  }
  :deep(span) {
    font-size: 16px;
    line-height: 28px;
  }
  :deep(ul), :deep(ol) {
    padding: 0;

    li {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
      max-width: 524px;
    }
  }
}

.locked-field {
  background-color: fade(@bg-brand-default, 5%);
}

.revision-badge {
  padding: 2px 8px;
  background-color: @grey-400;
  border-radius: 24px;
}

.revision-badge-quarter {
  background-color: @orange-100;
  color: @orange-500;
}

.current-version-badge {
  padding: 2px 6px;
  background-color: @bg-info-light;
}
</style>
